import AddIcon from "@mui/icons-material/Add";
import CampaignOutlinedIcon from "@mui/icons-material/CampaignOutlined";
import {
  Alert,
  Box,
  Button,
  Paper,
  Typography,
  type SxProps,
  type Theme,
} from "@mui/material";
import { useCallback, useState } from "react";
import PageLayout from "../../components/common/page-layout";
import {
  type OrganizationMessageFieldsFragment,
  useOrganizationMessageCountsQuery,
} from "../../generated/graphql";
import MessageDetail from "./components/message-detail";
import MessageFormDialog from "./components/message-form-dialog";
import MessageList from "./components/message-list";
import { MESSAGE_TABS, type MessageTab } from "./message-content";
import useMessagePages from "./use-message-pages";

const styles = {
  layout: {
    display: "grid",
    gridTemplateColumns: { xs: "1fr", lg: "minmax(0, 5fr) minmax(0, 7fr)" },
    gap: 3,
    alignItems: "start",
  },
  placeholder: {
    p: 6,
    textAlign: "center",
    color: "text.secondary",
  },
  placeholderIcon: { fontSize: 48, mb: 1 },
} satisfies Record<string, SxProps<Theme>>;

type FormTarget = {
  readonly message: OrganizationMessageFieldsFragment | null;
};

/**
 * Messages from Tenet to chosen companies or to every company: write, preview
 * the reach, send now or schedule, edit, resolve and withdraw (TMS-2704).
 */
const OrganizationMessagesPage = () => {
  const [tab, setTab] = useState<MessageTab>("ALL");
  const view = MESSAGE_TABS.find((option) => option.value === tab)?.view;
  const { messages, error, hasMore, loadingMore, loadMoreError, loadMore } =
    useMessagePages(view);
  const { data: countsData } = useOrganizationMessageCountsQuery({
    errorPolicy: "all",
  });
  const [selectedUuid, setSelectedUuid] = useState<string | null>(null);
  const [formTarget, setFormTarget] = useState<FormTarget | null>(null);

  const handleLoadMore = useCallback(() => {
    void loadMore();
  }, [loadMore]);
  const handleEdit = useCallback(
    (message: OrganizationMessageFieldsFragment) => {
      setFormTarget({ message });
    },
    [],
  );

  return (
    <PageLayout
      maxWidth={false}
      title="Organization messages"
      subtitle="Tell customers about outages and new features. Info shows in the bell; Warning and Critical also show a banner."
      actions={
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => {
            setFormTarget({ message: null });
          }}
        >
          New message
        </Button>
      }
    >
      {error !== undefined && (
        <Alert severity="error">Could not load messages: {error.message}</Alert>
      )}
      <Box sx={styles.layout}>
        <MessageList
          tab={tab}
          counts={countsData?.organizationMessageCounts ?? null}
          messages={messages}
          selectedUuid={selectedUuid}
          hasMore={hasMore}
          loadingMore={loadingMore}
          loadMoreError={loadMoreError}
          onTabChange={setTab}
          onSelect={setSelectedUuid}
          onLoadMore={handleLoadMore}
        />
        {selectedUuid === null ? (
          <Paper variant="outlined" sx={styles.placeholder}>
            <CampaignOutlinedIcon sx={styles.placeholderIcon} />
            <Typography>
              Select a message to see how it looks, who it reached and what
              happened to it.
            </Typography>
          </Paper>
        ) : (
          <MessageDetail uuid={selectedUuid} onEdit={handleEdit} />
        )}
      </Box>
      <MessageFormDialog
        open={formTarget !== null}
        message={formTarget?.message ?? null}
        onClose={() => {
          setFormTarget(null);
        }}
        onSaved={(uuid) => {
          setFormTarget(null);
          setSelectedUuid(uuid);
        }}
      />
    </PageLayout>
  );
};

export default OrganizationMessagesPage;
